import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { CONSENT_KEY, defaultConsent, getStoredConsent, saveConsent } from '../consent/consent';

export default function CookieBanner() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang === 'en' ? 'en' : 'it';

  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(defaultConsent);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs({ ...defaultConsent, ...stored });
    }
    const openHandler = () => {
      const s = getStoredConsent();
      setPrefs({ ...defaultConsent, ...(s || {}) });
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener('auronixio:open-cookie-preferences', openHandler);
    // Also watch localStorage removal.
    const iv = setInterval(() => {
      if (!window.localStorage.getItem(CONSENT_KEY) && !visible) setVisible(true);
    }, 4000);
    return () => {
      window.removeEventListener('auronixio:open-cookie-preferences', openHandler);
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptAll = () => {
    const c = { essential: true, functionality: true, analytics: true, marketing: true, personalization: true };
    saveConsent(c);
    setPrefs(c);
    setManaging(false);
    setVisible(false);
  };

  const rejectAll = () => {
    const c = { essential: true, functionality: false, analytics: false, marketing: false, personalization: false };
    saveConsent(c);
    setPrefs(c);
    setManaging(false);
    setVisible(false);
  };

  const savePrefs = () => {
    saveConsent(prefs);
    setManaging(false);
    setVisible(false);
  };

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const categories = useMemo(() => ([
    { key: 'essential', locked: true },
    { key: 'functionality', locked: false },
    { key: 'analytics', locked: false },
    { key: 'marketing', locked: false },
    { key: 'personalization', locked: false },
  ]), []);

  if (!visible) return null;

  return (
    <div className="auro-cookie" data-testid="cookie-banner" role="dialog" aria-live="polite" aria-label={t('cookies.bannerTitle')}>
      <div className="auro-cookie__card">
        {!managing && (
          <>
            <div className="auro-cookie__text">
              <h3>{t('cookies.bannerTitle')}</h3>
              <p>{t('cookies.bannerBody')}</p>
              <p className="auro-cookie__more">
                <NavLink to={`/${currentLang}/cookies`}>{t('cookies.more')}</NavLink>
              </p>
            </div>
            <div className="auro-cookie__actions">
              <button type="button" className="auro-btn auro-btn--ghost" onClick={() => setManaging(true)} data-testid="cookie-manage">{t('cookies.manage')}</button>
              <button type="button" className="auro-btn auro-btn--ghost" onClick={rejectAll} data-testid="cookie-reject">{t('cookies.rejectAll')}</button>
              <button type="button" className="auro-btn auro-btn--primary" onClick={acceptAll} data-testid="cookie-accept">{t('cookies.acceptAll')}</button>
            </div>
          </>
        )}

        {managing && (
          <div className="auro-cookie__prefs">
            <div className="auro-cookie__head">
              <h3>{t('cookies.prefs.title')}</h3>
              <p>{t('cookies.prefs.subtitle')}</p>
            </div>
            <ul>
              {categories.map(({ key, locked }) => (
                <li key={key}>
                  <div>
                    <h4>{t(`cookies.prefs.${key}.title`)}</h4>
                    <p>{t(`cookies.prefs.${key}.body`)}</p>
                  </div>
                  <label className={`auro-switch ${locked ? 'is-locked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={locked ? true : !!prefs[key]}
                      disabled={locked}
                      onChange={() => !locked && toggle(key)}
                      data-testid={`toggle-${key}`}
                    />
                    <span className="auro-switch__track"><span className="auro-switch__thumb" /></span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="auro-cookie__actions">
              <button type="button" className="auro-btn auro-btn--ghost" onClick={rejectAll}>{t('cookies.rejectAll')}</button>
              <button type="button" className="auro-btn auro-btn--ghost" onClick={acceptAll}>{t('cookies.acceptAll')}</button>
              <button type="button" className="auro-btn auro-btn--primary" onClick={savePrefs} data-testid="cookie-save">{t('cookies.save')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
